import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";

const AudioPlayer = ({ source }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    audioRef.current = new Audio(source);
    const audio = audioRef.current;

    const handleEnded = () => setIsPlaying(false);
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleError = () => setError(true);

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("error", handleError);

    return () => {
      audio.pause();
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("error", handleError);
    };
  }, [source]);

  const handleAudio = () => {
    if (!audioRef.current || error) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => setError(true));
    }
  };

  const handleSeek = (event) => {
    if (!audioRef.current || error) return;
    const nextTime = Number(event.target.value);
    audioRef.current.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (error) {
    return (
      <div
        style={{
          padding: "8px 12px",
          border: "1px solid #e9ecef",
          borderRadius: "10px",
          backgroundColor: "#fff3f3",
          color: "#d9534f",
          fontSize: "14px",
        }}
      >
        ❌ Audio cannot be played. (Unsupported browsers)
        <a href={source} download > Download</a>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "8px 12px",
        backgroundColor: "#f8f9fa",
        borderRadius: "20px",
        border: "1px solid #e9ecef",
      }}
    >
      <button
        onClick={handleAudio}
        style={{
          background: "#FF6666",
          border: "none",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "white",
        }}
      >
        {isPlaying ? <FaPause size={12} /> : <FaPlay size={12} />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <input
          type="range"
          min="0"
          max={Number.isFinite(duration) && duration > 0 ? duration : 0}
          step="0.1"
          value={Number.isFinite(currentTime) ? currentTime : 0}
          onChange={handleSeek}
          disabled={!duration || error}
          style={{
            width: "100%",
            accentColor: "#FF6666",
            cursor: duration ? "pointer" : "default",
          }}
        />
      </div>

      <div
        style={{
          fontSize: "12px",
          color: "#666",
          minWidth: "40px",
          textAlign: "right",
        }}
      >
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>
    </div>
  );
};

export default AudioPlayer;
